import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { pdf } from "@react-pdf/renderer";
import { Modal, Input, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import PdfModal from "../../../components/PdfModal";
import { LoadingScreen } from "../../../components/loader/LoadingScreen";
import { MaterialCountingPieChart } from "../../../components/charts/MaterialCountingPieChart";
import ContainerCompact from "../../container/ContainerCompact";
import { azureSASTokenToUrl } from "../../../components/formatted/FormattedAzureSasToken";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import { mergePdfs } from "../../../components/download/pdfUtils";
import { getAzureBlobFile, getTemporalData } from "../../../services/container";
import { reduceImageQuality } from "../../../shared/utils/utils";
import { buildCollectionRowChartData } from "../utils/buildCollectionRowChartData";
import { starsCalculation } from "../ActivityUtilities";
import {
  GenerateAndDownloadFillingCyclePdf,
  extractCollections,
  getUniqueHistoryCycle,
} from "../GenerateAndDownloadFillingCyclePdf";
import PdfImageSelectionModal from "../components/PdfImageSelectionModal";

/**
 * Shared compliance PDF / email export pipeline used by activity list rows
 * and the activity detail page. Owns off-screen chart refs and modal state.
 */
export const useActivityCollectionCompliancePdfExport = ({
  timeLine,
  superContainerTimeLine,
  historyCycle,
  files = [],
  clientRegisteredWeight,
  numberOfPackmat,
  comments,
  wasteName,
  clientName,
  dropOff,
  collectionFillingLevel,
}) => {
  const { t, i18n } = useTranslation();
  const collectionData = useSelector((state) => state.collection.data);
  const user = useMemo(() => {
    try {
      const { token: _token, ...rest } = JSON.parse(
        localStorage.getItem("user") || "{}",
      );
      return rest;
    } catch {
      return {};
    }
  }, []);
  const azureUrl = process.env.REACT_APP_AZURE_URL;

  const chartRef = useRef(null);
  const cycleChartRef = useRef(null);
  const materialChartRef = useRef(null);

  const [chartData, setChartData] = useState({});
  const [displayChartData, setDisplayChartData] = useState(false);
  const [openModalExport, setOpenModalExport] = useState(false);
  const [pdfFile, setPdfFile] = useState([]);
  const [initialPdfModalAction, setInitialPdfModalAction] = useState("export");
  const [reportingOptions, setReportingOptions] = useState(false);
  const [exportFiles, setExportFiles] = useState([]);
  const [openPdfImageSelectionModal, setOpenPdfImageSelectionModal] =
    useState(false);
  const [loadScreen, setLoadScreen] = useState(false);
  const [isProgress, setIsProgress] = useState(false);

  const openPdfImageSelection = useCallback(() => {
    setReportingOptions(false);
    setOpenPdfImageSelectionModal(true);
  }, []);

  const prepareChartForExport = useCallback(() => {
    setChartData(
      buildCollectionRowChartData({
        timeLine,
        superContainerTimeLine,
        collectionData,
        t,
      }),
    );
    setDisplayChartData(true);
  }, [collectionData, superContainerTimeLine, t, timeLine]);

  const startPdfExport = useCallback(
    (mode = "export") => {
      prepareChartForExport();
      setInitialPdfModalAction(mode === "share" ? "share" : "export");
      if (files?.length > 0) {
        setReportingOptions(true);
      } else {
        openPdfImageSelection();
      }
    },
    [files, openPdfImageSelection, prepareChartForExport],
  );

  const pdfImageSelectionData = useMemo(() => {
    if (!openPdfImageSelectionModal) {
      return { allImages: [], defaultSelectedUrls: new Set() };
    }
    const collections = extractCollections(
      superContainerTimeLine,
      collectionData,
      timeLine,
      historyCycle,
    );
    if (!collections?.length) {
      return { allImages: [], defaultSelectedUrls: new Set() };
    }
    const allImages = collections
      .flatMap((col, i) =>
        (col.historyCycle || []).map((item) => ({ item, collectionIndex: i })),
      )
      .sort((a, b) => new Date(a.item._time) - new Date(b.item._time));
    const defaultSelectedUrls = new Set(
      collections.flatMap((col) =>
        getUniqueHistoryCycle(col.historyCycle || []).map(
          (item) => item.image_url,
        ),
      ),
    );
    return { allImages, defaultSelectedUrls };
  }, [
    openPdfImageSelectionModal,
    superContainerTimeLine,
    collectionData,
    timeLine,
    historyCycle,
  ]);

  const generatePdf = useCallback(
    async (selectedImageUrls) => {
      setReportingOptions(false);
      setIsProgress(false);
      setLoadScreen(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const controller = new AbortController();
        const { pdfContent, pdfFilename } =
          await GenerateAndDownloadFillingCyclePdf({
            refs: { chartRef, cycleChartRef, materialChartRef },
            user,
            superContainerTimeLine,
            collectionData,
            timeLine,
            historyCycle,
            controller,
            t,
            formattedColorMaterial,
            getTemporalData,
            imageDimensions: async (src) => {
              try {
                const { default: reactImageSize } = await import(
                  "react-image-size"
                );
                if (!src) throw new Error("Image source is undefined or empty");
                return await reactImageSize(src);
              } catch (error) {
                console.error(
                  `Error getting image dimensions for ${src}:`,
                  error.message,
                );
                return { width: 0, height: 0 };
              }
            },
            reduceImageQuality,
            azureSASTokenToUrl,
            starsCalculation,
            clientRegisteredWeight,
            numberOfPackmat,
            comments,
            wasteName,
            clientName,
            dropOff,
            collectionFillingLevel,
            i18n,
            selectedImageUrls,
          });

        const mainPdfBlob = await pdf(pdfContent).toBlob();
        const pdfList = [{ filename: pdfFilename, pdfBlob: mainPdfBlob }];

        if (exportFiles?.length) {
          const azureFiles = await Promise.all(
            exportFiles.map(async (filePath) => {
              const { data: fileBlob } = await getAzureBlobFile({
                blobUrl: `${azureUrl}${filePath}`,
              });
              const fileName = filePath.split("/").pop();
              return { filename: fileName, pdfBlob: fileBlob };
            }),
          );

          pdfList.push(...azureFiles);
          const mergedPdfBlob = await mergePdfs(pdfList);
          setPdfFile([{ filename: pdfFilename, pdfBlob: mergedPdfBlob }]);
        } else {
          setPdfFile(pdfList);
        }
        setOpenModalExport(true);
        setOpenPdfImageSelectionModal(false);
        setExportFiles([]);
      } catch (error) {
        console.error("Failed to generate compliance PDF:", error);
        setOpenPdfImageSelectionModal(false);
      } finally {
        setDisplayChartData(false);
        setLoadScreen(false);
      }
    },
    [
      user,
      superContainerTimeLine,
      collectionData,
      timeLine,
      historyCycle,
      t,
      clientRegisteredWeight,
      numberOfPackmat,
      comments,
      wasteName,
      clientName,
      dropOff,
      collectionFillingLevel,
      i18n,
      exportFiles,
      azureUrl,
    ],
  );

  const closePdfImageSelection = useCallback(() => {
    setOpenPdfImageSelectionModal(false);
    setDisplayChartData(false);
  }, []);

  return {
    startPdfExport,
    uiProps: {
      timeLine,
      chartRef,
      cycleChartRef,
      materialChartRef,
      chartData,
      displayChartData,
      openModalExport,
      setOpenModalExport,
      pdfFile,
      setPdfFile,
      initialPdfModalAction,
      reportingOptions,
      setReportingOptions,
      exportFiles,
      setExportFiles,
      files,
      openPdfImageSelectionModal,
      pdfImageSelectionData,
      generatePdf,
      closePdfImageSelection,
      openPdfImageSelection,
      loadScreen,
      isProgress,
      setLoadScreen,
      t,
    },
  };
};

export const ActivityCollectionCompliancePdfExportUi = ({
  timeLine,
  chartRef,
  cycleChartRef,
  materialChartRef,
  chartData,
  displayChartData,
  openModalExport,
  setOpenModalExport,
  pdfFile,
  setPdfFile,
  initialPdfModalAction,
  reportingOptions,
  setReportingOptions,
  exportFiles,
  setExportFiles,
  files,
  openPdfImageSelectionModal,
  pdfImageSelectionData,
  generatePdf,
  closePdfImageSelection,
  openPdfImageSelection,
  loadScreen,
  isProgress,
  setLoadScreen,
  t,
}) => (
  <>
    {Object.keys(chartData || {}).length > 0 && displayChartData ? (
      <div
        aria-hidden
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: "1200px",
          opacity: 1,
          pointerEvents: "none",
          zIndex: -9999,
        }}
      >
        <div ref={chartRef} style={{ height: 600, width: 600 }}>
          <MaterialCountingPieChart
            dataChart={chartData.dataAnomalyDelivery}
            labelChart={chartData.labelsAnomalyDelivery}
            materialTotal={chartData.numberOfAnomaly}
            chartTotal={chartData.anomalyChartTotal}
            colorChart={chartData.colorAnomalyDelivery}
            showMaterialTotal={false}
            radius={["30%", "60%"]}
            centerLabelVariant="errors"
            labelFontSize="0.9rem"
          />
        </div>
        <article
          ref={cycleChartRef}
          className="bg-white rounded-xl"
          style={{ width: 800 }}
        >
          <ContainerCompact
            container={chartData.container}
            history={true}
            dataForCompactOverlay={chartData.historyCycle}
            findHistoryId={null}
            showPackmat={false}
            showLimitFillingLevel={false}
            showToolbox={false}
          />
        </article>
        <div ref={materialChartRef} style={{ height: 600, width: 600 }}>
          <MaterialCountingPieChart
            dataChart={chartData.dataNumberOfObject}
            labelChart={chartData.labelObjectSeparate}
            materialTotal={chartData.numberOfObjects}
            colorChart={chartData.colorObject}
            showMaterialTotal={false}
            showToolbox={false}
            radius={["30%", "60%"]}
            labelFontSize="0.9rem"
          />
        </div>
      </div>
    ) : null}

    <PdfImageSelectionModal
      isOpen={openPdfImageSelectionModal}
      onClose={closePdfImageSelection}
      allImages={pdfImageSelectionData.allImages}
      defaultSelectedUrls={pdfImageSelectionData.defaultSelectedUrls}
      azureSASTokenToUrl={azureSASTokenToUrl}
      onConfirm={generatePdf}
      t={t}
    />

    {openModalExport ? (
      <PdfModal
        data={timeLine}
        setOpenModalExport={setOpenModalExport}
        openModalExport={openModalExport}
        setLoadScreen={setLoadScreen}
        setPdfFile={setPdfFile}
        pdfFile={pdfFile}
        characterization={false}
        initialAction={initialPdfModalAction}
      />
    ) : null}

    <Modal className="w-1/3" isOpen={reportingOptions}>
      <ModalHeader>
        <span className="text-base text-blue">{t("Compliance report")}</span>
        <CloseIcon
          style={{
            color: "var(--gray)",
            cursor: "pointer",
          }}
          onClick={() => setReportingOptions(false)}
        />
      </ModalHeader>
      <ModalBody className="flex flex-col gap-2">
        <span>{t("Export options")}</span>
        {files?.length > 0
          ? files.map((file) => {
              const isSelected = exportFiles.includes(file);
              return (
                <article
                  key={file}
                  className="w-full flex flex-row items-center"
                >
                  <Input
                    className="information-data-check mt-auto"
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setExportFiles([...exportFiles, file]);
                      } else {
                        setExportFiles(exportFiles.filter((f) => f !== file));
                      }
                    }}
                    name={`import-${file}`}
                  />
                  <span>{t("Add file")}</span>
                  <span className="ml-1 text-blue">{file.split("/")[4]}</span>
                </article>
              );
            })
          : null}
      </ModalBody>
      <ModalFooter className="flex justify-center w-full p-2">
        <button
          type="button"
          className="p-2 text-md font-medium text-white rounded-lg"
          style={{ backgroundColor: "#00A7A6" }}
          onClick={openPdfImageSelection}
        >
          {t("generateReport")}
        </button>
      </ModalFooter>
    </Modal>

    {loadScreen ? <LoadingScreen isProgress={isProgress} progress={0} /> : null}
  </>
);
